<template>
  <div class="container" v-if="!isLoading">
    <div class="row">
      <div class="col-12">
        <TopNav class="mt-4" />
        <img class="mt-3 scale-2x" :srcset="`${logo} 3x`" />
        <p class="mt-4">
          {{ $t('SETTINGS.TEXT.CONNECT_YOUR_GOOGLE_MEET') }}
        </p>
        <HMActivatedTag
          v-if="isGoogleMeetConnected"
          :label="$t('SETTINGS.LABEL.CONNECTED')"
          class="my-3"
        />
      </div>
      <div class="col-12">
        <hr class="my-2" />
        <div
          class="d-flex flex-row justify-content-between align-items-center mt-4"
        >
          <h3>{{ $t('SETTINGS.LABEL.CONNECTED_ACCOUNT') }}</h3>
          <label
            v-if="!isGoogleMeetConnected"
            class="cursor-pointer"
            @click="showConnectModal"
          >
            {{ $t('SETTINGS.BUTTON.CONNECT_ACCOUNT')
            }}<i class="fal fa-plus c-coral ml-2"></i>
          </label>
        </div>
        <HMTable
          class="mt-3"
          :items="googleMeetConnections"
          :fields="googleMeetConnectionFields"
        >
          <template slot="name" slot-scope="{ data }">
            <span>{{ getConnectionName(data) }}</span>
          </template>
          <template slot="is_default" slot-scope="{ data }">
            <HMCheck v-model="data.default" />
          </template>
          <template slot="connected" slot-scope="{ data }">
            <span>{{ $formatDate(data.created) }}</span>
          </template>
          <template slot="edit" slot-scope="{ data }">
            <HMIconButton
              :icon="['far', 'ellipsis-v']"
              :id="`popover-${data.id}`"
            />
            <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
              <HMPopoverMenuItem
                v-if="!data.default"
                @click="setDefaultConnection(data.id)"
              >
                {{ $t('SETTINGS.LABEL.MAKE_DEFAULT') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem @click="showUpdateConnectionNameModal(data)">
                {{ $t('SETTINGS.LABEL.RENAME') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem
                v-if="canRemoveConnection(data)"
                v-confirm="{
                  title: $t('SETTINGS.TITLE.DISCONNECT_GOOGLE_MEET_CONNECTION'),
                  text: $t(
                    'SETTINGS.TEXT.DISCONNECT_GOOGLE_MEET_CONNECTION_DESCRIPTION'
                  ),
                  confirmButton: $t(
                    'SETTINGS.BUTTON.DISCONNECT_FROM_GOOGLE_MEET'
                  ),
                  confirm: () => removeConnection(data.id),
                }"
              >
                <span class="d-flex justify-content-between align-items-center">
                  {{ $t('SETTINGS.LABEL.DISCONNECT') }}
                  <i class="far fa-trash-alt c-coral ml-4"></i>
                </span>
              </HMPopoverMenuItem>
            </HMPopover>
          </template>
        </HMTable>
      </div>
      <div class="col-12 small mt-4">
        <div
          class="p-4 rounded bg-lightest-gray"
          v-html="$t('SETTINGS.TEXT.GOOGLE_API_DISCLAIMER')"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import TopNav from '@/web/components/navigation/TopNav.vue';
import { getConfig } from '@/web/utils/configuration';
import { mapGetters } from 'vuex';
import UpgradeYourPlanModal from '../../../components/settings/UpgradeYourPlanModal';
import { replaceRedirectURLWithCurrentDomain } from '../../../utils/url';
import { errorToMessage } from '../../../../core/utils/errors';
import { HMRenameModal } from '../../../components/common';
import { sanitize } from '@/core/utils/utils';
import ConnectGoogleModal from '../../../components/settings/ConnectGoogleModal';

export default Vue.extend({
  name: 'GoogleMeetIntegration',
  components: {
    TopNav,
  },
  computed: {
    ...mapGetters({
      teacher: 'user/teacher',
    }),
    applicationName() {
      return getConfig().applicationName;
    },
    isGoogleMeetConnected() {
      return this.googleMeetConnections?.length > 0;
    },
    googleMeetConnectionFields() {
      return [
        {
          name: 'name',
          title: this.$t('SETTINGS.TABLE.NAME'),
        },
        {
          name: 'is_default',
          title: this.$t('SETTINGS.TABLE.DEFAULT'),
        },
        {
          name: 'connected',
          title: this.$t('SETTINGS.TABLE.CONNECTED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
  },
  data() {
    return {
      logo: '/assets/images/integrations/google-meet.svg',
      isLoading: true,
      inProgress: false,
      googleMeetConnections: null,
      isInit: false,
      isSignIn: false,
    };
  },
  methods: {
    sanitize,
    canRemoveConnection(connection) {
      if (connection.default && this.googleMeetConnections?.length > 1) {
        return false;
      }

      return true;
    },
    getConnectionName(googleMeetConnection) {
      if (googleMeetConnection.email) {
        return `${googleMeetConnection.name} (${googleMeetConnection.email})`;
      }
      return googleMeetConnection.name;
    },
    showConnectModal() {
      this.$modal.show(ConnectGoogleModal, {
        title: this.$t('SETTINGS.TITLE.CONNECT_GOOGLE_MEET'),
        description: this.$t('SETTINGS.TEXT.CONNECT_GOOGLE_MEET_DESCRIPTION'),
        connectFunction: this.connect,
      });
    },
    async connect() {
      const redirectURL = replaceRedirectURLWithCurrentDomain(
        process.env.VUE_APP_GOOGLE_MEET_REDIRECT_URL
      );
      const rootUrl = `https://accounts.google.com/o/oauth2/v2/auth`;

      const options = {
        redirect_uri: redirectURL,
        client_id: process.env.VUE_APP_GOOGLE_MEET_CLIENT_ID,
        access_type: 'offline',
        response_type: 'code',
        prompt: 'consent',
        scope: [
          'https://www.googleapis.com/auth/meetings.space.created', // For creating meet spaces
          'https://www.googleapis.com/auth/userinfo.email',
          'https://www.googleapis.com/auth/userinfo.profile',
        ].join(' '),
      };

      const qs = new URLSearchParams(options);
      window.location = `${rootUrl}?${qs.toString()}`;
    },
    checkForOAuthCode() {
      const code = this.$route.query.code;
      const scope = this.$route.query.scope;
      if (!code) return;
      const query = { ...this.$route.query };
      delete query.code;
      delete query.scope;
      this.$router.replace({ query });

      this.addGoogleMeetConnection(code, scope);
    },
    async addGoogleMeetConnection(code, scope) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/addGoogleMeetConnection', {
          code,
          scope,
        });
        this.googleMeetConnections = await this.$store.dispatch(
          'setting/getGoogleMeetConnections'
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_MEET_SUCCESSFULLY_CONNECTED'
          ),
        });
        this.$store.dispatch('user/loadUserInfo');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to connect to Google Meet',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async setDefaultConnection(connectionId) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(
          'setting/setGoogleMeetDefaultConnection',
          connectionId
        );
        this.googleMeetConnections = await this.$store.dispatch(
          'setting/getGoogleMeetConnections'
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_MEET_SUCCESSFULLY_UPDATED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    showUpdateConnectionNameModal(conection) {
      this.$modal.show(
        HMRenameModal,
        {
          value: conection.name,
        },
        {},
        {
          renamed: async name => {
            await this.updateConnectionName(conection.id, name);
          },
        }
      );
    },
    async updateConnectionName(connectionId, name) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/setGoogleMeetConnectionName', {
          connectionId,
          name,
        });
        this.googleMeetConnections = await this.$store.dispatch(
          'setting/getGoogleMeetConnections'
        );
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_MEET_SUCCESSFULLY_UPDATED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async removeConnection(conectionId) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(
          'setting/removeGoogleMeetConnection',
          conectionId
        );
        this.googleMeetConnections = await this.$store.dispatch(
          'setting/getGoogleMeetConnections'
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_MEET_SUCCESSFULLY_REMOVED'
          ),
        });
        this.$store.dispatch('user/loadUserInfo');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    this.$store.dispatch('user/loadUserInfo');
    this.googleMeetConnections = await this.$store.dispatch(
      'setting/getGoogleMeetConnections'
    );
    this.checkForOAuthCode();
    this.isLoading = false;
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.cursor-pointer {
  cursor: pointer;
}

.scale-2x {
  height: 54px;
}
</style>
